import { useState, useCallback, useRef } from 'react';
import { runPreflight, PreflightTest } from 'twilio-video';
import { useApiRequests } from 'hooks';
import { productName } from 'config/productProfile';

export type StepStatus = 'idle' | 'running' | 'passed' | 'failed' | 'warning';
export type OverallResult = 'pass' | 'warning' | 'fail' | null;

export interface TestStep {
  id: 'browser' | 'devices' | 'network' | 'bandwidth';
  status: StepStatus;
  detailKey: string;
  detailParams?: Record<string, string | number>;
  detailKey2?: string;
  detailParams2?: Record<string, string | number>;
}

const INITIAL_STEPS: TestStep[] = [
  { id: 'browser', status: 'idle', detailKey: '' },
  { id: 'devices', status: 'idle', detailKey: '' },
  { id: 'network', status: 'idle', detailKey: '' },
  { id: 'bandwidth', status: 'idle', detailKey: '' },
];

const PREFLIGHT_TIMEOUT_MS = 45000;
const UPLOAD_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

function formatSpeed(mbps: number): string {
  if (mbps >= 1) return `${mbps.toFixed(1)} Mbps`;
  return `${Math.round(mbps * 1000)} kbps`;
}

export function useConnectionTest() {
  const { getDiagnosticsToken, speedTestDownload, speedTestUpload } = useApiRequests();
  const [steps, setSteps] = useState<TestStep[]>(INITIAL_STEPS);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [overallResult, setOverallResult] = useState<OverallResult>(null);
  const preflightRef = useRef<PreflightTest | null>(null);
  const abortedRef = useRef(false);

  const patchStep = useCallback(
    (id: TestStep['id'], patch: Partial<Omit<TestStep, 'id'>>) =>
      setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s))),
    [],
  );

  const reset = useCallback(() => {
    abortedRef.current = true;
    preflightRef.current?.stop();
    preflightRef.current = null;
    setSteps(INITIAL_STEPS);
    setIsRunning(false);
    setIsDone(false);
    setOverallResult(null);
  }, []);

  const stopTest = useCallback(() => {
    abortedRef.current = true;
    preflightRef.current?.stop();
    preflightRef.current = null;
    setSteps(INITIAL_STEPS);
    setIsRunning(false);
    setIsDone(true);
    setOverallResult(null);
  }, []);

  const startTest = useCallback(async () => {
    abortedRef.current = false;
    preflightRef.current?.stop();
    setSteps(INITIAL_STEPS);
    setIsRunning(true);
    setIsDone(false);
    setOverallResult(null);

    let failCount = 0;
    let warnCount = 0;

    // ── Step 1: Browser support ─────────────────────────────────────────────
    patchStep('browser', { status: 'running', detailKey: '' });
    const hasWebRTC = !!(window.RTCPeerConnection && navigator.mediaDevices?.getUserMedia);
    if (!hasWebRTC) {
      patchStep('browser', { status: 'failed', detailKey: 'detailBrowserFail' });
      failCount++;
      setOverallResult('fail');
      setIsDone(true);
      setIsRunning(false);
      return;
    }
    patchStep('browser', { status: 'passed', detailKey: 'detailBrowserOk' });

    // ── Step 2: Camera & microphone ─────────────────────────────────────────
    patchStep('devices', { status: 'running', detailKey: '' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach((t) => t.stop());
      if (abortedRef.current) return;
      patchStep('devices', { status: 'passed', detailKey: 'detailDevicesOk' });
    } catch (err: any) {
      if (abortedRef.current) return;
      failCount++;
      const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      patchStep('devices', {
        status: 'failed',
        detailKey: denied ? 'detailDevicesDenied' : 'detailDevicesFail',
      });
    }

    // ── Fetch diagnostics token ─────────────────────────────────────────────
    let token = '';
    try {
      const data = await getDiagnosticsToken();
      if (abortedRef.current) return;
      token = data.token;
    } catch {
      if (abortedRef.current) return;
      const networkKey = navigator.onLine ? 'detailNetworkFail' : 'detailNetworkOffline';
      const networkParams = navigator.onLine ? { productName } : undefined;
      patchStep('network', { status: 'failed', detailKey: networkKey, detailParams: networkParams });
      patchStep('bandwidth', { status: 'failed', detailKey: 'detailBandwidthFail' });
      setOverallResult('fail');
      setIsDone(true);
      setIsRunning(false);
      return;
    }

    // ── Step 3: Network preflight ───────────────────────────────────────────
    patchStep('network', { status: 'running', detailKey: '' });
    await new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const preflight = runPreflight(token);
      preflightRef.current = preflight;

      const timer = setTimeout(() => {
        preflight.stop();
        if (!abortedRef.current) {
          const key = navigator.onLine ? 'detailNetworkFail' : 'detailNetworkOffline';
          const params = navigator.onLine ? { productName } : undefined;
          patchStep('network', { status: 'failed', detailKey: key, detailParams: params });
          failCount++;
        }
        settle();
      }, PREFLIGHT_TIMEOUT_MS);

      preflight.on('completed', (report) => {
        clearTimeout(timer);
        if (!abortedRef.current) {
          const isTurn = report.selectedIceCandidatePairStats?.localCandidate?.candidateType === 'relay';
          if (isTurn) {
            patchStep('network', { status: 'warning', detailKey: 'detailNetworkTurn' });
            warnCount++;
          } else {
            patchStep('network', { status: 'passed', detailKey: 'detailNetworkOk', detailParams: { productName } });
          }
        }
        settle();
      });

      preflight.on('failed', () => {
        clearTimeout(timer);
        if (!abortedRef.current) {
          const key = navigator.onLine ? 'detailNetworkFail' : 'detailNetworkOffline';
          const params = navigator.onLine ? { productName } : undefined;
          patchStep('network', { status: 'failed', detailKey: key, detailParams: params });
          failCount++;
        }
        settle();
      });
    });

    if (abortedRef.current) return;

    // ── Step 4: Bandwidth (download + upload) ───────────────────────────────
    patchStep('bandwidth', { status: 'running', detailKey: '', detailKey2: undefined });

    let downloadKey = 'detailDownloadFail';
    let downloadParams: Record<string, string | number> | undefined;
    let downloadStatus: StepStatus = 'failed';

    try {
      const { bytes, durationMs } = await speedTestDownload();
      if (abortedRef.current) return;
      const mbps = (bytes * 8) / (durationMs / 1000) / 1_000_000;
      const speed = formatSpeed(mbps);
      if (mbps >= 4) {
        downloadStatus = 'passed';
        downloadKey = 'detailDownloadGood';
        downloadParams = { speed };
      } else if (mbps >= 1) {
        downloadStatus = 'warning';
        downloadKey = 'detailDownloadFair';
        downloadParams = { speed };
        warnCount++;
      } else {
        downloadStatus = 'failed';
        downloadKey = 'detailDownloadPoor';
        downloadParams = { speed };
        failCount++;
      }
    } catch {
      if (abortedRef.current) return;
      failCount++;
    }

    let uploadKey = 'detailUploadFail';
    let uploadParams: Record<string, string | number> | undefined;
    let uploadStatus: StepStatus = 'failed';

    try {
      const { durationMs } = await speedTestUpload(UPLOAD_SIZE_BYTES);
      if (abortedRef.current) return;
      const mbps = (UPLOAD_SIZE_BYTES * 8) / (durationMs / 1000) / 1_000_000;
      const speed = formatSpeed(mbps);
      if (mbps >= 2) {
        uploadStatus = 'passed';
        uploadKey = 'detailUploadGood';
        uploadParams = { speed };
      } else if (mbps >= 0.5) {
        uploadStatus = 'warning';
        uploadKey = 'detailUploadFair';
        uploadParams = { speed };
        warnCount++;
      } else {
        uploadStatus = 'failed';
        uploadKey = 'detailUploadPoor';
        uploadParams = { speed };
        failCount++;
      }
    } catch {
      if (abortedRef.current) return;
      failCount++;
    }

    const bandwidthStatus: StepStatus =
      downloadStatus === 'failed' || uploadStatus === 'failed'
        ? 'failed'
        : downloadStatus === 'warning' || uploadStatus === 'warning'
        ? 'warning'
        : 'passed';

    patchStep('bandwidth', {
      status: bandwidthStatus,
      detailKey: downloadKey,
      detailParams: downloadParams,
      detailKey2: uploadKey,
      detailParams2: uploadParams,
    });

    if (abortedRef.current) return;

    // ── Overall result ──────────────────────────────────────────────────────
    const overall: OverallResult = failCount > 0 ? 'fail' : warnCount > 0 ? 'warning' : 'pass';
    setOverallResult(overall);
    setIsDone(true);
    setIsRunning(false);
  }, [getDiagnosticsToken, speedTestDownload, speedTestUpload, patchStep]);

  return { steps, isRunning, isDone, overallResult, startTest, stopTest, reset };
}
